import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import * as notificationsApi from '../api/notifications';
import {
  notificationKeys,
  type CenterNotification,
} from '../api/notifications';
import { useAuth } from './useAuth';
import {
  NOTIFICATION_BUS_EVENT,
  OdooBusClient,
  type BusNotificationEnvelope,
} from '../lib/odooBus';

function isNotificationPayload(
  payload: unknown
): payload is CenterNotification {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    typeof (payload as CenterNotification).id === 'number' &&
    typeof (payload as CenterNotification).title === 'string'
  );
}

export function useNotifications() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const centerId = user?.center?.id;
  const enabled = Boolean(centerId);

  const listQuery = useQuery({
    queryKey: notificationKeys.list(),
    queryFn: () => notificationsApi.listNotifications(),
    enabled,
  });

  const unreadQuery = useQuery({
    queryKey: notificationKeys.unreadCount,
    queryFn: () => notificationsApi.getUnreadCount(),
    enabled,
  });

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    const bus = new OdooBusClient();
    const unsubscribe = bus.subscribe((envelopes: BusNotificationEnvelope[]) => {
      let appended = false;
      for (const envelope of envelopes) {
        if (envelope.message?.type !== NOTIFICATION_BUS_EVENT) {
          continue;
        }
        const payload = envelope.message.payload;
        if (!isNotificationPayload(payload) || payload.is_read) {
          continue;
        }
        queryClient.setQueryData<CenterNotification[]>(
          notificationKeys.list(),
          (prev) => {
            const current = prev ?? [];
            if (current.some((n) => n.id === payload.id)) {
              return current;
            }
            appended = true;
            return [payload, ...current];
          }
        );
      }
      if (appended) {
        void queryClient.invalidateQueries({
          queryKey: notificationKeys.unreadCount,
        });
      }
    });
    bus.start();
    return () => {
      unsubscribe();
      bus.stop();
    };
  }, [enabled, centerId, queryClient]);

  const markReadMutation = useMutation({
    mutationFn: (id: number) => notificationsApi.markNotificationRead(id),
    onSuccess: (updated) => {
      queryClient.setQueryData<CenterNotification[]>(
        notificationKeys.list(),
        (prev) =>
          (prev ?? []).map((n) => (n.id === updated.id ? updated : n))
      );
      void queryClient.invalidateQueries({
        queryKey: notificationKeys.unreadCount,
      });
    },
  });

  const notifications = useMemo(
    () => listQuery.data ?? [],
    [listQuery.data]
  );

  return {
    notifications,
    unreadCount: unreadQuery.data ?? 0,
    isLoading: listQuery.isPending || unreadQuery.isPending,
    markRead: (id: number) => markReadMutation.mutateAsync(id),
    isMarkingRead: markReadMutation.isPending,
  };
}
